import { makeStyles } from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import { useAsync } from '../common';
import { SearchBox, SectionLabel } from '../components';
import { useCurrentUser } from '../current-user';
import { palette, tokens } from '../theme';
import {
    ASSIGNED_GROUP_LABEL,
    isTodoWorkItem,
    listParticipationWorkItems,
    PARTICIPATED_GROUP_LABEL,
    partitionByAssignment,
    TodoFilterToggle,
    useWorkItemSearch,
    type WorkItem,
    WorkItemResultRow,
} from '../work-items';

export type QuickAddSectionProps = {
    date: string;
    selected: WorkItem | null;
    onSelect: (item: WorkItem) => void;
};

export const QuickAddSection: React.FC<QuickAddSectionProps> = ({ date, selected, onSelect }) => {
    const classes = useStyles();
    const { user } = useCurrentUser();
    const teamMemberId = user?.id ?? '';
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));

    const [query, setQuery] = useState('');
    const [includeTodo, setIncludeTodo] = useState(false);
    const searching = query.trim().length > 0;
    const { results, loading: searchInFlight } = useWorkItemSearch(query, 200);

    // With no query typed, open with the same iteration items the backfill
    // dialog suggests; the search box narrows to a title match once used.
    const defaults = useAsync(
        () => (teamMemberId ? listParticipationWorkItems(teamMemberId, year, month) : Promise.resolve([] as WorkItem[])),
        [teamMemberId, year, month],
    );

    const suggestions = defaults.status === 'success' ? defaults.data : [];
    const todoCount = useMemo(() => suggestions.filter((i) => isTodoWorkItem(i.state)).length, [suggestions]);

    // Suggestions hide not-started items until the filter is on. A typed search
    // is an explicit request for a specific item, so it stays unfiltered.
    const visibleSuggestions = useMemo(
        () => (includeTodo ? suggestions : suggestions.filter((i) => !isTodoWorkItem(i.state))),
        [suggestions, includeTodo],
    );

    const items = searching ? results : visibleSuggestions;
    const loading = searching ? searchInFlight : defaults.status === 'loading';

    const groups = useMemo(() => partitionByAssignment(items), [items]);
    const grouped = !searching && groups.assigned.length > 0 && groups.participated.length > 0;

    return (
        <div className={classes.root}>
            <SectionLabel
                text="Quick add"
                trailing={<TodoFilterToggle checked={includeTodo} hiddenCount={todoCount} onChange={setIncludeTodo} />}
            />

            <SearchBox value={query} onChange={setQuery} />

            <div className={classes.results}>
                {loading ? (
                    <p className={classes.searchingText}>{searching ? 'Searching…' : 'Loading…'}</p>
                ) : items.length === 0 ? null : (
                    <div className={classes.resultsScroll}>
                        {grouped ? (
                            <>
                                <SectionLabel text={ASSIGNED_GROUP_LABEL} />
                                {groups.assigned.map((item) => (
                                    <WorkItemResultRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={onSelect} />
                                ))}
                                <div className={classes.groupGap}>
                                    <SectionLabel text={PARTICIPATED_GROUP_LABEL} />
                                </div>
                                {groups.participated.map((item) => (
                                    <WorkItemResultRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={onSelect} />
                                ))}
                            </>
                        ) : (
                            items.map((item) => (
                                <WorkItemResultRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={onSelect} />
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
    },
    results: {
        flexGrow: 1,
        minHeight: 0,
        marginTop: tokens.spacingVerticalS,
    },
    resultsScroll: {
        height: '100%',
        overflowY: 'auto',
        paddingRight: tokens.spacingHorizontalS,
    },
    searchingText: {
        margin: `${tokens.spacingVerticalS} 0 0 0`,
        fontSize: '12px',
        color: palette.dimText,
    },
    groupGap: {
        marginTop: tokens.spacingVerticalM,
    },
});
